import { ERROR_CODES, type ErrorCode } from "@onsemble/shared/error-codes";
import i18n from "@/lib/i18n";
import {
  GENERIC_ERROR_MESSAGE,
  parseApiError,
  type ParsedApiError,
} from "@/lib/api-error";

/**
 * Friendly copy per code, keyed into the locale files' `errors` section.
 * Upstream/bug codes always use this copy (their server messages describe the
 * failure, not what the user should do); expected codes use it only as a
 * fallback when the server didn't send a message.
 */
const CODE_MESSAGE_KEYS: Partial<Record<ErrorCode, string>> = {
  VALIDATION_ERROR: "errors.validation",
  INVALID_REFERENCE: "errors.validation",
  BAD_REQUEST: "errors.validation",
  UNAUTHENTICATED: "errors.unauthenticated",
  FORBIDDEN: "errors.forbidden",
  NO_ORG_ACCESS: "errors.noOrgAccess",
  NOT_FOUND: "errors.notFound",
  DUPLICATE_RESOURCE: "errors.duplicate",
  RATE_LIMITED: "errors.rateLimited",
  HAJOCA_ERROR: "errors.hajocaUnavailable",
  HAJOCA_NOT_AUTHORIZED: "errors.hajocaUnavailable",
  HAJOCA_TIMEOUT: "errors.hajocaUnavailable",
  TAX_UPSTREAM_ERROR: "errors.taxUnavailable",
  LLM_ERROR: "errors.processingUnavailable",
  LLM_TIMEOUT: "errors.processingUnavailable",
  EMAIL_ERROR: "errors.emailUnavailable",
  SMS_ERROR: "errors.smsUnavailable",
  AUTH_UPSTREAM_ERROR: "errors.authUnavailable",
};

function defaultMessage(parsed: ParsedApiError): string {
  const base = i18n.t("errors.default");
  return parsed.requestId
    ? `${base}${i18n.t("errors.ref", { requestId: parsed.requestId })}`
    : base;
}

/**
 * Turn a ParsedApiError into a message safe to show a tech-averse user:
 * plain language, no status codes or stack traces, actionable where possible.
 */
export function userErrorMessage(parsed: ParsedApiError): string {
  if (parsed.isNetworkError) return i18n.t("errors.network");

  const serverMessage =
    parsed.message !== GENERIC_ERROR_MESSAGE ? parsed.message : "";

  if (parsed.code) {
    const { class: errorClass } = ERROR_CODES[parsed.code];
    // Expected-class messages are written for users — show them verbatim
    // (e.g. ORG_NOT_CONFIGURED explains exactly what setup is missing).
    if (errorClass === "expected" && serverMessage) return serverMessage;
    const key = CODE_MESSAGE_KEYS[parsed.code];
    if (key) return i18n.t(key);
    return defaultMessage(parsed);
  }

  // Legacy response without a code: a 4xx body message is still user-facing
  // prose; anything else (5xx, HTML pages) gets the generic copy.
  if (
    parsed.status >= 400 &&
    parsed.status < 500 &&
    serverMessage &&
    !serverMessage.startsWith("<")
  ) {
    return serverMessage;
  }
  return defaultMessage(parsed);
}

type ToastInput = {
  title: string;
  description: string;
  variant: "destructive";
};

/**
 * Common call-site pattern: parse an unknown error and show it as a
 * destructive toast with a friendly description. Returns the parsed error so
 * callers can also log/track it.
 */
export function toastApiError(
  toastFn: (props: ToastInput) => unknown,
  e: unknown,
  fallbackTitle?: string,
): ParsedApiError {
  const parsed = parseApiError(e);
  toastFn({
    title: fallbackTitle ?? i18n.t("common.error"),
    description: userErrorMessage(parsed),
    variant: "destructive",
  });
  return parsed;
}
